import React, { useCallback, useEffect, useRef, useState } from 'react';

// 🧠📖 "what i remember" (PORTED_HOME_HANDOFF_2026_09_25 wave 2 C2): the
// resident's memories of THIS room, on the owner's name page of his journal.
// Older chat that scrolled out of his view lives on as memories (the server's
// scribe); the owner reads them here and can make him forget one (✕). Nina:
// the list is for forgetting, not a consent gate; he keeps what happened AND
// how he feels about the owner. Owner-only: the words come from the owner's
// own secret-proven POST (chatGameApi.ownerMemory), never a status or a row.
//
// Voice rule: the header is in HIS hand (pj-head, Caveat); each memory line
// is his (pj-line); the app's lines are printed (pj-printed). The classes are
// the journal's (this mounts inside PortedJournal, after the special moments).
// Dark: while the server answers {ok:false, reason:'off'} nothing is drawn at
// all (a hidden control reads as a missing feature; an absent one does not).

const PAGE = { list: {} };

/**
 * The owner's memories controller. `enabled` = the owner's place page; `api`
 * = chatGameApi (injectable for the tests). load() runs when the name page
 * opens (the component's mount), more() pages back, forget(id) is optimistic
 * and undone if the server says no.
 */
export function usePortedMemories({ gameId, playerId, enabled, api }) {
  const [state, setState] = useState({ items: [], next: null, loaded: false, loading: false, off: false, resident: null });
  const live = useRef(true);
  const nextRef = useRef(null); // the page cursor, read by more() (a state updater may be deferred)
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  // R4 (09-26): the hook is mounted in EVERY room (GameStepPage calls it
  // unconditionally); off the owner's place page the reset must not setState
  // (a new object = one extra whole-page render per mount / seat change).
  useEffect(() => {
    if (!enabled) return;
    nextRef.current = null;
    setState({ items: [], next: null, loaded: false, loading: false, off: false, resident: null });
  }, [gameId, playerId, enabled]);

  const fetchPage = useCallback((cursor) => {
    if (!enabled || !gameId || !playerId || !api) return Promise.resolve(null);
    setState((s) => (s.loading ? s : { ...s, loading: true }));
    return api.ownerMemory(gameId, playerId, cursor ? { list: { cursor } } : PAGE)
      .then((r) => {
        const m = r && r.owner_memory;
        if (!live.current) return null;
        if (!m || m.ok === false) {
          setState((s) => ({ ...s, loading: false, loaded: true, off: !!(m && m.reason === 'off') }));
          return null;
        }
        const got = Array.isArray(m.memories) ? m.memories.filter((x) => x && typeof x.id === 'string' && typeof x.text === 'string') : [];
        nextRef.current = m.next || null;
        setState((s) => {
          const have = new Set(cursor ? s.items.map((x) => x.id) : []);
          const items = cursor ? [...s.items, ...got.filter((x) => !have.has(x.id))] : got;
          return { items, next: m.next || null, loaded: true, loading: false, off: false, resident: m.resident || s.resident };
        });
        return m;
      })
      .catch(() => { if (live.current) setState((s) => ({ ...s, loading: false })); return null; });
  }, [enabled, gameId, playerId, api]);

  const load = useCallback(() => fetchPage(null), [fetchPage]);
  const more = useCallback(() => (nextRef.current ? fetchPage(nextRef.current) : Promise.resolve(null)), [fetchPage]);
  const forget = useCallback((id) => {
    if (!enabled || !gameId || !playerId || !api || typeof id !== 'string') return Promise.resolve(false);
    setState((s) => ({ ...s, items: s.items.map((x) => (x.id === id ? { ...x, gone: true } : x)) }));
    return api.ownerMemory(gameId, playerId, { forget: id })
      .then((r) => {
        const ok = !!(r && r.owner_memory && r.owner_memory.ok);
        if (!ok && live.current) setState((s) => ({ ...s, items: s.items.map((x) => (x.id === id ? { ...x, gone: false } : x)) }));
        return ok;
      })
      .catch(() => { if (live.current) setState((s) => ({ ...s, items: s.items.map((x) => (x.id === id ? { ...x, gone: false } : x)) })); return false; });
  }, [enabled, gameId, playerId, api]);

  return { ...state, load, more, forget };
}

export default function PortedMemories({ memories = null, onForget = null, name = 'Your resident' }) {
  const load = memories && memories.load;
  // the list is fetched when this page OPENS (this mounts with the name tab)
  useEffect(() => { if (typeof load === 'function') load(); }, [load]);
  if (!memories || memories.off) return null;
  const items = Array.isArray(memories.items) ? memories.items : [];
  const who = String(name || '').trim() || 'Your resident';
  return (
    <div className="pj-mem" data-ported-memories="">
      <div className="pj-head">what i remember</div>
      {items.length ? (
        <>
          {items.map((m) => (
            <div className="pj-fact" key={m.id}>
              <div className={`pj-line${m.gone ? ' pj-gone' : ''}`}>{m.text}</div>
              {onForget && !m.gone ? (
                <button type="button" className="pj-forget" aria-label={`Forget: ${m.text}`} onClick={() => onForget(m.id)}>✕</button>
              ) : null}
            </div>
          ))}
          <div className="pj-printed">✕ makes {who} forget it. The chat itself stays.</div>
          {memories.next ? (
            <button type="button" className="pj-link" disabled={!!memories.loading} onClick={() => memories.more && memories.more()}>older memories</button>
          ) : null}
        </>
      ) : (memories.loaded ? <div className="pj-printed">Nothing yet. Older chat lands here as it scrolls away.</div> : null)}
    </div>
  );
}
